/* eslint-disable react-hooks/exhaustive-deps */
import { useMediaQuery, Grid, Box, useTheme, Typography, Button, Container, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomRightImage from "@components/CustomRightImagev2";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { useEffect, useState } from "react";
import { TaxSystemConfigResponse } from "@interfaces/index";
import { amountToText, calculateAmountMonthly, formatCurrency } from "@helpers/funciones";
import icon_check from "@assets/img/icon_check_cotizador.svg";
import { StyledSlider } from "@components/CustomSlider";

import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { ButtonTypeSelectedCustom } from "@components/ButtonTypeSelectedCustom";
import { MarketStructure } from "@interfaces/LeasingTypes";
import { useLazyGetLeasingTypeConfigQuery } from "@api/catalogs";
import { LEASING_TYPE_CODES } from "@helpers/constantes";

const CotizadorVivemoto = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();

  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [getLeasingTypeConfig] = useLazyGetLeasingTypeConfigQuery();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [configCreditoSimple, setConfigCreditoSimple] = useState<TaxSystemConfigResponse | null>(null);
  const [configArrendamiento, setConfigArrendamiento] = useState<TaxSystemConfigResponse | null>(null);
  const [creditType, setCreditType] = useState<string>("credito_simple");
  const [leasingTypeId, setLeasingTypeId] = useState<string>("");

  const [amountMarks, setAmountMarks] = useState<{ value: number; label: string }[]>([]);
  const [termMarks, setTermMarks] = useState<{ value: number; label: string }[]>([]);

  const [cotizadorConfig, setCotizadorConfig] = useState<TaxSystemConfigResponse>({
    id: "",
    code: "",
    product_id: "",
    minAmount: 0,
    maxAmount: 0,
    stepAmount: 0,
    minTerm: 0,
    maxTerm: 0,
    stepTerm: 0,
    defaultAmount: 0,
    defaultTerm: 0,
    tax_system_type: "",
    defaultAdvance: 0,
    maxAdvance: 0,
    minAdvance: 0,
    stepAdvance: 0,
  });

  const [selectedAmount, setSelectedAmount] = useState<number>(cotizadorConfig.defaultAmount);
  const [selectedTerm, setSelectedTerm] = useState<number>(cotizadorConfig.defaultTerm);
  const [monthlyRate, setMonthlyRate] = useState<number>(0);

  const [advancePercentageMarket, setAdvancePercentageMarket] = useState<MarketStructure[]>([]);
  const [anticipo, setAnticipo] = useState<number>(20);
  const [residual] = useState<number>(0.5);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [responseCreditoSimple, responseArrendamiento] = await Promise.all([
          getTaxSystemConfig("CVMPF").unwrap(),
          getTaxSystemConfig("CVAPF").unwrap(),
        ]);
        setConfigCreditoSimple(responseCreditoSimple);
        setConfigArrendamiento(responseArrendamiento);
        const responseLeasingTypeConfig = await getLeasingTypeConfig({
          tax_system_id: responseArrendamiento.id,
        }).unwrap();

        if (responseLeasingTypeConfig.length > 0) {
          const leasingTypeId =
            responseLeasingTypeConfig.find((item) => item.leasing_type.code === LEASING_TYPE_CODES.MOTOCICLETAS)
              ?.leasing_type_id || "";
          setLeasingTypeId(leasingTypeId);
        }
      } catch (error) {
        console.error("Error fetching tax system configs", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (creditType === "credito_simple" && configCreditoSimple) {
      setCotizadorConfig(configCreditoSimple);
    } else if (creditType === "arrendamiento" && configArrendamiento) {
      setCotizadorConfig(configArrendamiento);
    }
  }, [creditType, configCreditoSimple, configArrendamiento]);

  useEffect(() => {
    const { minTerm, maxTerm, stepTerm, defaultAdvance, defaultTerm, defaultAmount, minAmount, maxAmount, stepAmount } =
      cotizadorConfig;
    if (minTerm < maxTerm && stepTerm > 0) {
      const marks = [];
      for (let i = minTerm; i <= maxTerm; i += stepTerm) {
        marks.push({ value: i, label: `${i}` });
      }

      const maxAdvance = cotizadorConfig.maxAdvance ?? 0;
      const minAdvance = cotizadorConfig.minAdvance ?? 0;
      const stepAdvance = cotizadorConfig.stepAdvance ?? 0;

      const listAdvance: MarketStructure[] = [];

      if (stepAdvance > 0 && minAdvance > 0 && maxAdvance >= minAdvance) {
        for (let r = minAdvance; r <= maxAdvance; r += stepAdvance) {
          listAdvance.push({ value: r, label: `${r}%` });
        }
        if (listAdvance.length && listAdvance[listAdvance.length - 1].value !== maxAdvance) {
          listAdvance.push({ value: maxAdvance, label: `${maxAdvance}%` });
        }
      }

      if (minAmount < maxAmount && stepAmount > 0) {
        const marks = [];

        marks.push({ value: minAmount, label: amountToText(minAmount) });
        marks.push({ value: maxAmount, label: amountToText(maxAmount) });

        setAmountMarks(marks);
      }

      setAdvancePercentageMarket(listAdvance);
      setSelectedTerm(defaultTerm);
      setTermMarks(marks);
      setSelectedAmount(defaultAmount);
      setAnticipo(defaultAdvance || 30);
    }
  }, [cotizadorConfig]);

  useEffect(() => {
    if (selectedAmount && selectedTerm) {
      let amountToCalculate = selectedAmount;

      // Restar anticipo para ambos productos; para arrendamiento también restar residual
      const advanceRate = anticipo ? anticipo / 100 : 0;
      const residualRate = residual ? residual / 100 : 0;

      if (creditType === "arrendamiento") {
        const financed = selectedAmount * (1 - advanceRate - residualRate);
        amountToCalculate = financed > 0 ? financed : 0;
      } else {
        const financed = selectedAmount * (1 - advanceRate);
        amountToCalculate = financed > 0 ? financed : 0;
      }

      const monthly = calculateAmountMonthly(
        amountToCalculate,
        selectedTerm,
        interfaceConfig?.interest_rate,
        interfaceConfig?.comision,
        interfaceConfig?.iva,
      );
      setMonthlyRate(monthly);
    }
  }, [
    selectedAmount,
    selectedTerm,
    interfaceConfig?.interest_rate,
    interfaceConfig?.comision,
    interfaceConfig?.iva,
    creditType,
    anticipo,
    residual,
  ]);

  const setConditions = () => {
    if (!cotizadorConfig.product_id || !selectedAmount || !selectedAmount || !monthlyRate) {
      return;
    }
    const sourceCode = sessionStorage.getItem("source_code");

    const urlType = creditType === "credito_simple" ? "creditosimple" : "arrendamiento";

    let urlPortal = `/producto/${urlType}/crear_cuenta?tax_system_id=${cotizadorConfig.id}&product_id=${cotizadorConfig.product_id}&person_type=${cotizadorConfig.code}&loan_amount=${selectedAmount}&loan_term=${selectedTerm}&tax_system_type=${cotizadorConfig.tax_system_type}`;

    if (creditType === "arrendamiento") {
      urlPortal += `&advance_percentage=${anticipo}&residual_percentage=${residual}`;
    }

    if (creditType === "credito_simple") {
      urlPortal += `&advance_percentage=${anticipo}`;
    }

    if (leasingTypeId && creditType === "arrendamiento") {
      urlPortal += `&leasing_type_id=${leasingTypeId}`;
    }

    if (sourceCode) {
      urlPortal += `&source_code=${sourceCode}`;
    }
    navigate(urlPortal);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sourceCode = params.get("code");

    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, []);

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8}>
          <Container maxWidth={"sm"}>
            <Typography fontWeight={500} variant="h3" mt={4} color={theme?.palette?.primary?.dark} textAlign={"center"}>
              Cotizador
            </Typography>

            <Box>
              <Box>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  mt={1}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona tu tipo de crédito
                </Typography>

                <Grid container spacing={isMobileOrTablet ? 1 : 2} mt={1} mr={isMobileOrTablet ? 1 : 0}>
                  <Grid item xs={6} md={6} display="flex" justifyContent={"flex-end"}>
                    {!isLoading ? (
                      <ButtonTypeSelectedCustom
                        title="Crédito"
                        onClick={() => setCreditType("credito_simple")}
                        isSelected={creditType === "credito_simple"}
                        isMovile={isMobileOrTablet}
                        height={"60px"}
                        heightMoviles="60px"
                        width={"170px"}
                        widthMoviles="130px"
                        icon={icon_check}
                        width_icon="22px"
                        height_icon="22px"
                        font_size={"12px"}
                        top_icon="-12px"
                        font_size_movil="11px"
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? 150 : 190}
                        height={isMobileOrTablet ? 60 : 70}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6} md={6} display="flex" justifyContent={"flex-start"}>
                    {!isLoading ? (
                      <ButtonTypeSelectedCustom
                        title="Arrendamiento"
                        onClick={() => setCreditType("arrendamiento")}
                        isSelected={creditType === "arrendamiento"}
                        isMovile={isMobileOrTablet}
                        height={"60px"}
                        heightMoviles="60px"
                        width={"170px"}
                        widthMoviles="130px"
                        icon={icon_check}
                        width_icon="22px"
                        height_icon="22px"
                        font_size={"12px"}
                        top_icon="-12px"
                        font_size_movil="11px"
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? 150 : 190}
                        height={isMobileOrTablet ? 60 : 70}
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>

              <Box mt={4} ml={isMobileOrTablet ? 3 : 1} mr={isMobileOrTablet ? 3 : 1}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona el precio de la moto
                </Typography>

                <Typography
                  fontWeight={700}
                  fontSize={"13px"}
                  mt={1}
                  color={theme?.palette?.primary?.main}
                  textAlign={"center"}
                >
                  {formatCurrency(selectedAmount)}
                </Typography>

                {!isLoading ? (
                  <StyledSlider
                    aria-label="amount"
                    min={cotizadorConfig.minAmount}
                    max={cotizadorConfig.maxAmount}
                    step={cotizadorConfig.stepAmount}
                    marks={amountMarks}
                    defaultValue={cotizadorConfig.defaultAmount}
                    value={selectedAmount}
                    onChange={(_event, newValue) => setSelectedAmount(newValue as number)}
                  />
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Box>

              <Box mb={1} ml={isMobileOrTablet ? 3 : 1} mr={isMobileOrTablet ? 3 : 1}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  mb={2}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona el plazo en meses
                </Typography>

                {!isLoading ? (
                  <StyledSlider
                    aria-label="term"
                    min={cotizadorConfig.minTerm}
                    max={cotizadorConfig.maxTerm}
                    step={cotizadorConfig.stepTerm}
                    defaultValue={cotizadorConfig.defaultTerm}
                    marks={termMarks}
                    value={selectedTerm}
                    onChange={(_event, newValue) => setSelectedTerm(newValue as number)}
                    valueLabelDisplay="on"
                  />
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Box>

              <Box mb={1} ml={isMobileOrTablet ? 3 : 1} mr={isMobileOrTablet ? 3 : 1}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  mb={2}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Anticipo
                </Typography>

                {!isLoading ? (
                  <StyledSlider
                    aria-label="advance"
                    min={cotizadorConfig.minAdvance}
                    max={cotizadorConfig.maxAdvance}
                    step={cotizadorConfig.stepAdvance}
                    defaultValue={cotizadorConfig.defaultAdvance}
                    marks={advancePercentageMarket}
                    value={anticipo}
                    onChange={(_event, newValue) => setAnticipo(newValue as number)}
                    valueLabelDisplay="on"
                  />
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Box>

              <Typography
                fontWeight={700}
                fontSize={"16px"}
                color={theme?.palette?.primary?.main}
                textAlign={"center"}
                m={4}
              >
                Pago mensual estimado*
              </Typography>

              <Typography
                mb={2}
                textAlign={"center"}
                variant="h4"
                fontWeight={theme?.palette?.fontWeights?.semibold}
                color={theme?.palette?.primary?.dark}
              >
                {isNaN(monthlyRate) || !isFinite(monthlyRate) ? "0" : formatCurrency(monthlyRate)}{" "}
                <Typography
                  variant="h5"
                  component={"span"}
                  fontWeight={theme?.palette?.fontWeights?.semibold}
                  color={theme?.palette?.primary?.dark}
                >
                  MXN
                </Typography>
              </Typography>

              <Typography
                textAlign={"center"}
                fontWeight={theme?.palette?.fontWeights?.light}
                color={theme?.palette?.primary?.dark}
                mt={2}
                mb={2}
              >
                <small>
                  *Esta simulación es para fines demostrativos y está sujeto a aprobación, las condiciones como la tasa,
                  el monto del crédito, o la comisión por apertura, podrían cambiar dependiendo el perfil y la situación
                  actual del solicitante.
                </small>
              </Typography>

              <Box display="flex" justifyContent="center" mb={2}>
                <Button variant="contained" onClick={setConditions} disabled={isLoading}>
                  Continuar
                </Button>
              </Box>
            </Box>
          </Container>
        </Grid>
        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>
    </>
  );
};
export default CotizadorVivemoto;
